/**
 * SDL Login/Landing Screen
 * First screen players see - sign in options + play as guest
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { LoginDiceScene } from '../scenes/LoginDiceScene';

export class LoginScreen extends EventEmitter {
  public element: HTMLElement;
  private diceScene: LoginDiceScene | null = null;

  constructor() {
    super();
    this.element = this.createElement();
  }

  private createElement(): HTMLElement {
    const screen = document.createElement('div');
    screen.className = 'sdl-login-screen';
    screen.innerHTML = `
      <!-- Atmospheric layers -->
      <div class="login-smoke-layer"></div>
      <div class="login-haze"></div>
      <div class="login-ambient-glow login-glow-cyan"></div>
      <div class="login-ambient-glow login-glow-magenta"></div>
      <div class="login-ambient-glow login-glow-gold"></div>
      <div class="login-vignette"></div>

      <!-- Logo -->
      <div class="login-logo-area">
        <img src="/assets/images/logo.png" alt="Street Dice Legends" class="login-logo-image" />
        <div class="login-logo-reflection"></div>
        <div class="login-tagline">Roll the dice. Run the streets.</div>
      </div>

      <!-- 3D Dice Scene -->
      <div class="login-dice-scene"></div>

      <!-- Auth Section -->
      <div class="login-auth-section">
        <button class="login-btn-social login-btn-apple" data-action="apple">
          <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
            <path d="M17.0703 17.1562C16.7422 17.9141 16.3555 18.6133 15.9082 19.2598C15.2949 20.1445 14.7949 20.7676 14.4141 21.1289C13.8281 21.6992 13.1992 21.9922 12.5234 22.0098C12.043 22.0098 11.4609 21.873 10.7832 21.5957C10.1035 21.3203 9.47656 21.1836 8.90039 21.1836C8.29688 21.1836 7.65234 21.3203 6.96484 21.5957C6.27734 21.873 5.71875 22.0176 5.28516 22.0332C4.63672 22.0625 3.99219 21.7617 3.34961 21.1289C2.93945 20.7402 2.41992 20.0977 1.79297 19.2012C1.12109 18.2441 0.570312 17.1328 0.140625 15.8652C-0.320312 14.4902 -0.554688 13.1582 -0.554688 11.8672C-0.554688 10.3945 -0.240234 9.12109 0.390625 8.05078C0.882812 7.19141 1.53516 6.53125 2.35156 6.06836C3.16797 5.60547 4.04883 5.36914 4.99805 5.35352C5.50781 5.35352 6.17578 5.51172 7.00586 5.82227C7.83398 6.13379 8.37305 6.29199 8.62109 6.29199C8.80859 6.29199 9.40625 6.10547 10.4004 5.73438C11.3379 5.39062 12.1309 5.24805 12.7832 5.30273C14.5527 5.44531 15.8789 6.14844 16.7559 7.41602C15.1758 8.37109 14.3945 9.72266 14.4102 11.4648C14.4258 12.8398 14.9238 13.9844 15.9004 14.8926C16.3379 15.3105 16.8262 15.6328 17.3711 15.8594C17.2773 16.1172 17.1777 16.3652 17.0703 16.6035V17.1562ZM12.8906 0.410156C12.8906 1.49609 12.4902 2.51172 11.6953 3.44922C10.7344 4.5625 9.57422 5.20312 8.31641 5.09961C8.30078 4.96875 8.29102 4.83008 8.29102 4.68359C8.29102 3.64258 8.74805 2.52734 9.55859 1.61523C9.96289 1.15234 10.4746 0.771484 11.0938 0.472656C11.7109 0.177734 12.2949 0.0136719 12.8438 -0.00195312C12.8594 0.138672 12.8906 0.275391 12.8906 0.410156Z" fill="black"/>
          </svg>
          Sign in with Apple
        </button>

        <button class="login-btn-social login-btn-google" data-action="google">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Sign in with Google
        </button>

        <div class="login-divider">
          <div class="login-divider-line"></div>
          <span class="login-divider-text">or</span>
          <div class="login-divider-line"></div>
        </div>

        <button class="login-btn-guest" data-action="guest">PLAY AS GUEST</button>

        <p class="login-footnote">No account needed. Progress saved locally.</p>
      </div>
    `;

    // Event listeners
    screen.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('[data-action]');
      if (!target) return;
      const action = (target as HTMLElement).dataset.action;

      switch (action) {
        case 'guest':
          this.emit('guest-login', undefined);
          break;
        case 'google':
          this.emit('google-login', undefined);
          break;
        case 'apple':
          this.emit('apple-login', undefined);
          break;
      }
    });

    return screen;
  }

  show(): void {
    this.element.style.display = 'flex';

    // Initialize 3D dice scene
    const sceneContainer = this.element.querySelector('.login-dice-scene') as HTMLElement;
    if (sceneContainer && !this.diceScene) {
      this.diceScene = new LoginDiceScene(sceneContainer);
    }
  }

  /** Resolves when the dice scene has loaded and rendered its first frame */
  async waitForReady(): Promise<void> {
    await this.diceScene?.ready;
  }

  hide(): void {
    this.element.style.opacity = '0';
    setTimeout(() => {
      this.element.style.display = 'none';
    }, 400);
  }

  destroy(): void {
    this.diceScene?.dispose();
    this.diceScene = null;
    this.element.remove();
  }
}
